import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { updateUserProfile } from '../../util/api/user.js';
import ProfilePictureUpload from '../../components/profile/ProfilePictureUpload.js';
import { isNativeApp } from '../../util/platformDetection';

/**
 * Mobile-optimized EditProfile component
 * Reuses web validation logic while providing touch-friendly mobile interface
 */
const EditProfileMobile = ({ user, onClose, onSave }) => {
  // Profile data state
  const [profile, setProfile] = useState({
    status: user?.credentials?.status || "",
    bio: user?.credentials?.biography || "",
    youtube: user?.credentials?.youtubeLink || "",
    tiktok: user?.credentials?.tiktokLink || "",
    instagram: user?.credentials?.instagramLink || "",
    twitter: user?.credentials?.twitterLink || ""
  });

  // Photo URLs
  const [photoUrl, setPhotoUrl] = useState(user?.credentials?.photoUrl || "");
  const [pendingPhotoUrl, setPendingPhotoUrl] = useState(user?.credentials?.pendingPhotoUrl || "");

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [activeSection, setActiveSection] = useState('basic'); // 'basic', 'social', 'photo'

  // Validation errors
  const [errors, setErrors] = useState({
    youtube: "",
    tiktok: "",
    instagram: "",
    twitter: ""
  });

  // Character count for status
  const [characters, setCharacters] = useState(profile.status?.length || 0);

  // Handle input changes
  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    
    if (field === 'status') {
      setCharacters(value.length);
    }
  };

  // Validate social media link (reusing web logic)
  const validateLink = (link, platformName) => {
    const domainRegex = /www\.([^.]+)\.com\/.*/;
    const match = platformName.match(domainRegex);
    const domain = match ? match[1] : null;

    if (!domain) {
      return "Invalid platform format";
    }

    const start = "https://";
    const domainSite = "www." + domain + ".com/";

    let processedLink = link;
    if (link.toLowerCase().startsWith('https://')) {
      processedLink = link.substring(8);
    } else if (link.toLowerCase().startsWith('http://')) {
      processedLink = link.substring(7);
    }

    const lower = processedLink.toLowerCase();
    const index = lower.indexOf(domainSite);

    if (index === -1) {
      return "Link must include " + domainSite;
    }

    let url = processedLink.substring(index + domainSite.length);

    if (url.toLowerCase().includes(domainSite)) {
      return "Invalid link";
    }

    // Remove extra '/'
    let i = 0;
    while (url.charAt(i) === "/") {
      url = url.substring(1);
      i++;
    }

    return start.concat(domainSite, url);
  };

  // Handle form submission
  const handleSubmit = async () => {
    setIsLoading(true);
    let isValid = true;
    const newData = {};

    // Clear previous errors
    setErrors({
      youtube: "",
      tiktok: "",
      instagram: "",
      twitter: ""
    });

    // Social media links configuration
    const links = {
      youtube: { name: 'www.youtube.com/username', value: profile.youtube },
      tiktok: { name: 'www.tiktok.com/username', value: profile.tiktok },
      instagram: { name: 'www.instagram.com/username', value: profile.instagram },
      twitter: { name: 'www.twitter.com/username', value: profile.twitter }
    };

    // Validate social media links
    Object.keys(links).forEach((social) => {
      const originalValue = user?.credentials?.[social + 'Link'] || '';
      
      if (links[social].value !== originalValue) {
        if (!links[social].value) {
          // Empty link is valid
          switch (social) {
            case 'youtube':
              newData.youtubeLink = "";
              break;
            case 'tiktok':
              newData.tiktokLink = "";
              break;
            case 'instagram':
              newData.instagramLink = "";
              break;
            case 'twitter':
              newData.twitterLink = "";
              break;
          }
          return;
        }

        const validate = validateLink(links[social].value, links[social].name);
        
        if (!validate.startsWith("https://")) {
          setErrors(prev => ({ ...prev, [social]: validate }));
          isValid = false;
          return;
        }

        // Add valid link as pending
        switch (social) {
          case 'youtube':
            newData.pendingYoutubeLink = [validate, false];
            break;
          case 'tiktok':
            newData.pendingTikTokLink = [validate, false];
            break;
          case 'instagram':
            newData.pendingInstagramLink = [validate, false];
            break;
          case 'twitter':
            newData.pendingTwitterLink = [validate, false];
            break;
        }
      }
    });

    // Check status changes
    if (profile.status !== (user?.credentials?.status || '')) {
      if (!profile.status) {
        newData.status = "";
      } else {
        newData.pendingStatus = [profile.status, false];
      }
    }

    // Check bio changes
    if (profile.bio !== (user?.credentials?.biography || '')) {
      if (!profile.bio) {
        newData.biography = "";
      } else {
        newData.pendingBiography = [profile.bio, false];
      }
    }

    if (!isValid) {
      setIsLoading(false);
      return;
    }

    // Submit changes if valid
    if (Object.keys(newData).length > 0) {
      try {
        await updateUserProfile(newData);
        setShowSuccessMessage(true);
        if (onSave) onSave();
        
        setTimeout(() => {
          setShowSuccessMessage(false);
          if (onClose) onClose();
        }, 2000);
      } catch (error) {
        console.error('Error updating profile:', error);
      }
    }

    setIsLoading(false);
  };

  // Handle profile picture upload
  const handleProfilePictureUpload = (newPendingPhotoUrl) => {
    setPendingPhotoUrl(newPendingPhotoUrl);
  };

  // Render section navigation
  const renderSectionTabs = () => (
    <div style={{
      display: 'flex',
      borderBottom: '1px solid #333',
      backgroundColor: '#1a1a1a'
    }}>
      {[
        { id: 'basic', label: 'Basic' },
        { id: 'social', label: 'Social' },
        { id: 'photo', label: 'Photo' }
      ].map(section => (
        <button
          key={section.id}
          onClick={() => setActiveSection(section.id)}
          style={{
            flex: 1,
            padding: '12px',
            border: 'none',
            backgroundColor: activeSection === section.id ? '#333' : 'transparent',
            color: activeSection === section.id ? 'gold' : '#888',
            fontSize: '16px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {section.label}
        </button>
      ))}
    </div>
  );

  // Render basic info section
  const renderBasicSection = () => (
    <div style={{ padding: '20px' }}>
      {/* Status */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ color: '#ccc', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
          Status
        </label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={profile.status}
            onChange={(e) => handleChange('status', e.target.value)}
            placeholder="Shown beside @Username on your profile"
            maxLength={15}
            style={{
              width: '100%',
              padding: '12px 16px',
              backgroundColor: '#ffffff',
              border: '2px solid #ccc',
              borderRadius: '8px',
              color: '#000000',
              fontSize: '16px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
          <div style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '12px',
            color: characters >= 13 ? '#ff9900' : '#888'
          }}>
            {15 - characters}
          </div>
        </div>
      </div>

      {/* Biography */}
      <div>
        <label style={{ color: '#ccc', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
          Biography
        </label>
        <textarea
          value={profile.bio}
          onChange={(e) => handleChange('bio', e.target.value)}
          placeholder="Tell the world about yourself and your chess journey."
          rows={6}
          style={{
            width: '100%',
            padding: '12px 16px',
            backgroundColor: '#ffffff',
            border: '2px solid #ccc',
            borderRadius: '8px',
            color: '#000000',
            fontSize: '16px',
            outline: 'none',
            resize: 'vertical',
            lineHeight: '1.4',
            boxSizing: 'border-box'
          }}
        />
        <br />
      </div>
    </div>
  );

  // Render social links section
  const renderSocialSection = () => {
    const socialPlatforms = [
      { key: 'youtube', label: 'YouTube', icon: 'fa-youtube', color: '#ff0000' },
      { key: 'tiktok', label: 'TikTok', icon: 'fa-tiktok', color: '#000000' },
      { key: 'instagram', label: 'Instagram', icon: 'fa-instagram', color: '#e4405f' },
      { key: 'twitter', label: 'Twitter', icon: 'fa-x-twitter', color: '#1da1f2' }
    ];

    return (
      <div style={{ padding: '20px' }}>
        {socialPlatforms.map(platform => (
          <div key={platform.key} style={{ marginBottom: '20px' }}>
            <label style={{
              color: '#ccc',
              fontSize: '14px',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className={`fa-brands ${platform.icon}`} style={{ color: platform.color }}></i>
              {platform.label}
            </label>
            <input
              type="text"
              value={profile[platform.key]}
              onChange={(e) => handleChange(platform.key, e.target.value)}
              placeholder={`www.${platform.key}.com/username`}
              style={{
                width: '100%',
                padding: '12px 16px',
                backgroundColor: '#ffffff',
                border: errors[platform.key] ? '2px solid #ff0050' : '2px solid #ccc',
                borderRadius: '8px',
                color: '#000000',
                fontSize: '16px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
            {errors[platform.key] && (
              <div style={{
                color: '#ff0000',
                fontSize: '12px',
                marginTop: '4px'
              }}>
                {errors[platform.key]}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Render photo section
  const renderPhotoSection = () => (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <ProfilePictureUpload
        userId={user?.credentials?.userId}
        photoUrl={photoUrl}
        pendingPhotoUrl={pendingPhotoUrl}
        setPhotoUrl={setPhotoUrl}
        setPendingPhotoUrl={setPendingPhotoUrl}
        size="large"
        onUploadComplete={handleProfilePictureUpload}
      />
    </div>
  );

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.95)',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '100vh',
      overflow: 'hidden',
      paddingTop: 'env(safe-area-inset-top)' // iPhone notch safe area
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#1a1a1a',
        padding: '15px 20px',
        borderBottom: '1px solid #333',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <h2 style={{ color: 'gold', margin: 0, fontSize: '18px' }}>Edit Profile</h2>
        <button
          onClick={onClose}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            color: '#ff0050',
            fontSize: '24px',
            cursor: 'pointer',
            padding: '5px'
          }}
        >
          ✕
        </button>
      </div>

      {/* Success Message */}
      {showSuccessMessage && (
        <div style={{
          backgroundColor: '#4caf50',
          color: 'white',
          padding: '12px 20px',
          textAlign: 'center',
          fontSize: '14px'
        }}>
          Profile changes sent for review!
        </div>
      )}

      {/* Section Tabs */}
      {renderSectionTabs()}

      {/* Content */}
      <div style={{ 
        flex: 1, 
        overflow: 'auto', 
        backgroundColor: '#121212',
        maxHeight: 'calc(100vh - 140px)' /* Account for header and footer */
      }}>
        {activeSection === 'basic' && renderBasicSection()}
        {activeSection === 'social' && renderSocialSection()}
        {activeSection === 'photo' && renderPhotoSection()}
      </div>

      {/* Footer with Cancel and Save Buttons */}
      <div style={{
        backgroundColor: '#1a1a1a',
        padding: '20px 20px',
        paddingBottom: '25px',
        borderTop: '1px solid #333',
        display: 'flex',
        gap: '10px'
      }}>
        <button
          onClick={onClose}
          style={{
            flex: 1,
            padding: '12px',
            backgroundColor: 'transparent',
            color: '#888',
            border: '1px solid #666',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={isLoading}
          style={{
            flex: 1,
            padding: '12px',
            backgroundColor: isLoading ? '#666' : '#ff0050',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: isLoading ? 'default' : 'pointer',
            opacity: isLoading ? 0.7 : 1
          }}
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user
});

export default connect(mapStateToProps)(EditProfileMobile);